import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useGlobalCommunicationCategoryCounts } from '../../../hooks/useCommunicationCategoryCounts';
import { CHART_RESIZE_DEBOUNCE_MS } from '../../../utils/chartFormatting';
import { categoryTone } from '../practice/communications/categories';
import { CardErrorState } from './CardErrorState';

// Accounts Overview hub's "Communications by category" card - a donut (the
// global, no-practice-filter counterpart to the practice-scoped
// CommunicationsByCategoryCard, which keeps its bar rows). Segment colours
// come from the shared category tone map (categories.ts), same tones the
// bars use elsewhere.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full';
const WINDOW_DAYS = 30;
// Donut is scaled down from a taller stacked layout so it sits side by side
// with the legend inside the card's (shorter, flex-allotted) body without
// forcing the body's scrollbar to kick in for a typical 3-8 category legend.
const DONUT_SIZE = 140;
const INNER_RADIUS = 48;
const OUTER_RADIUS = 68;
const LEGEND_SKELETON_ROWS = 4;

function DonutSkeleton() {
    return (
        <div className="flex items-stretch gap-4 h-full">
            <div
                className="rounded-full bg-gray-100 animate-pulse shrink-0 self-center"
                style={{ width: DONUT_SIZE, height: DONUT_SIZE }}
            />
            <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
                {Array.from({ length: LEGEND_SKELETON_ROWS }).map((_, i) => (
                    <div key={i} className="h-3 rounded bg-gray-100 animate-pulse" />
                ))}
            </div>
        </div>
    );
}

interface DonutSlice {
    name: string;
    value: number;
    color: string;
}

interface DonutTooltipProps {
    active?: boolean;
    payload?: { payload: DonutSlice }[];
}

function DonutTooltip({ active, payload }: DonutTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;
    const slice = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: slice.color }} />
            <span className="text-[12px] font-semibold text-gray-800">{slice.name}</span>
            <span className="text-[12px] text-gray-500 tabular-nums">{slice.value}</span>
        </div>
    );
}

interface HubCategoryCardProps {
    className?: string;
}

export function HubCategoryCard({ className = '' }: HubCategoryCardProps) {
    const { counts, isLoading, isError, refetch } =
        useGlobalCommunicationCategoryCounts(WINDOW_DAYS);
    const total = counts.reduce((sum, c) => sum + c.count, 0);

    const rows: DonutSlice[] = useMemo(
        () =>
            counts.map((c) => ({
                name: c.category,
                value: c.count,
                color: categoryTone(c.category)
            })),
        [counts]
    );

    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <h3 className="text-[15px] font-bold text-gray-800">Communications by category</h3>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    Last 30 days
                </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-4">
                {isLoading ? (
                    <DonutSkeleton />
                ) : isError ? (
                    <CardErrorState
                        message="Couldn't load communications by category"
                        onRetry={() => void refetch()}
                    />
                ) : rows.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">
                        No categorized communications in the last 30 days.
                    </p>
                ) : (
                    <div className="flex items-stretch gap-4 h-full">
                        <div
                            className="relative shrink-0 self-center"
                            style={{ width: DONUT_SIZE, height: DONUT_SIZE }}
                        >
                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                                debounce={CHART_RESIZE_DEBOUNCE_MS}
                                className="overflow-x-clip"
                            >
                                <PieChart>
                                    <Pie
                                        data={rows}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={INNER_RADIUS}
                                        outerRadius={OUTER_RADIUS}
                                        paddingAngle={2}
                                        strokeWidth={0}
                                    >
                                        {rows.map((row) => (
                                            <Cell key={row.name} fill={row.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<DonutTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-lg font-bold text-gray-900 tabular-nums">
                                    {total}
                                </span>
                                <span className="text-[11px] text-gray-400">logged / 30 days</span>
                            </div>
                        </div>
                        {/* No justify-center: centring a scrollable flex
                            column pushes overflow past the start edge, where
                            scrolling cannot reach it. Focusable for the same
                            reason as CommunicationsPerPersonCard. */}
                        <div
                            className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-2"
                            tabIndex={0}
                            role="list"
                            aria-label="Communications by category"
                        >
                            {rows.map((row) => (
                                <div key={row.name} className="flex items-center gap-2">
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ background: row.color }}
                                    />
                                    <span className="text-[12px] text-gray-600 flex-1 truncate">
                                        {row.name}
                                    </span>
                                    <span className="text-[12px] font-semibold text-gray-700 tabular-nums">
                                        {row.value}
                                    </span>
                                    <span className="text-[11px] text-gray-400 tabular-nums w-9 text-right">
                                        {total > 0 ? Math.round((row.value / total) * 100) : 0}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
